/**
 * @fileoverview SMS send hook - sends via API proxy, returns optimistic Communication.
 * @agentic connections
 */

// Hooks
import { useState, useCallback } from "react";
import { useMedplum, useMedplumProfile } from "@medplum/react";

import { ensureActiveSession } from "@/utils/auth/ensureActiveSession";
import { getApiEndpoint, getApiAuthHeaders } from "@/utils/api/getApiEndpoint";

// Types
import type { Communication, PractitionerRole } from "@medplum/fhirtypes";
import {
  buildCommunicationTypeCategory,
  buildMediumCoding,
} from "@benchmark/core/constants";
import type { UseSendSMSResult, SendSMSParams } from "@/types/domain/messaging";

/**
 * Sends SMS via API proxy with Medplum token; returns optimistic Communication for UI.
 * @agentic connections
 */
export function useSendSMS(): UseSendSMSResult {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as PractitionerRole | undefined;

  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const sendSMS = useCallback(
    async (params: SendSMSParams): Promise<Communication> => {
      const { patientId, message, fromNumber, toNumber, conversationId } =
        params;

      if (!profile?.id) {
        throw new Error(
          "User profile not available. Please ensure you are logged in.",
        );
      }

      if (!message.trim()) {
        throw new Error("Message content cannot be empty.");
      }

      if (!patientId) {
        throw new Error("Patient ID is required to send a message.");
      }

      if (!fromNumber || !toNumber) {
        throw new Error("Phone numbers are required to send a message.");
      }

      setIsSending(true);
      setError(null);

      try {
        // Ensure session is active and get a fresh access token
        const accessToken = await ensureActiveSession(medplum);

        // Call SMS API endpoint with Medplum token
        // The endpoint is routed differently per environment:
        // - Dev: Vite proxy → API Gateway (proxy adds API key)
        // - Staging/Prod: Direct API Gateway call (function adds API key)
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 20000);

        const apiUrl = getApiEndpoint("/api/v1/sms/send");

        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Medplum-Authorization": `Bearer ${accessToken}`,
            ...getApiAuthHeaders(),
          },
          body: JSON.stringify({
            from: fromNumber,
            to: toNumber,
            text: message,
            ...(conversationId && { conversationId }),
          }),
          signal: controller.signal,
        }).finally(() => clearTimeout(timeout));

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.error ||
              `Failed to send SMS: ${response.status} ${response.statusText}`,
          );
        }

        const result = await response.json();

        // Create an optimistic Communication object to display immediately.
        // A 2xx response means Dialpad has accepted the message, so we treat it
        // as sent (`completed`) rather than pending — the webhook-created
        // resource is only reconciled afterwards via targeted polling. Mirroring
        // the webhook's `completed` status also keeps dedup seamless (see
        // `toCommunication`). This avoids the bubble lingering in "Sending…"
        // for the full webhook round-trip (or indefinitely if it never lands).
        const optimisticCommunication: Communication = {
          resourceType: "Communication",
          id: `temp-${Date.now()}`, // Temporary ID for optimistic UI
          status: "completed", // Dialpad accepted the send; poll reconciles canonical record
          // Mirror the canonical SMS webhook shape: text channel in `medium`,
          // `patient-message` interaction type in `category`.
          category: buildCommunicationTypeCategory(
            "patient-message",
          ) as Communication["category"],
          medium: buildMediumCoding("text") as Communication["medium"],
          subject: {
            reference: `Patient/${patientId}`,
          },
          recipient: [
            {
              reference: `Patient/${patientId}`,
            },
          ],
          sender: {
            reference: `PractitionerRole/${profile.id}`,
            display: profile.practitioner?.display || "You",
          },
          payload: [
            {
              contentString: message,
            },
          ],
          sent: new Date().toISOString(),
          ...(conversationId && {
            identifier: [
              {
                system: "dialpad:conversationId",
                value: conversationId,
              },
            ],
          }),
          // Store Dialpad message ID if available
          ...(result.messageId && {
            identifier: [
              {
                system: "dialpad:messageId",
                value: result.messageId,
              },
              ...(conversationId
                ? [
                    {
                      system: "dialpad:conversationId",
                      value: conversationId,
                    },
                  ]
                : []),
            ],
          }),
        };

        setIsSending(false);

        // Return the optimistic message
        // The UI should start polling after this to get the real message from the webhook
        return optimisticCommunication;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to send message. Please try again.";

        const errorObj = new Error(errorMessage);
        setError(errorObj);
        setIsSending(false);
        throw errorObj;
      }
    },
    [medplum, profile],
  );

  return {
    sendSMS,
    isSending,
    error,
  };
}
